import { formatMoneyCompact, formatNumber } from "@/lib/format";
import type { ContractorPreview } from "@/types/domain";
import type { SaleCopy } from "./contractorLine";

type PreviewState = {
  data?: ContractorPreview;
  isError: boolean;
  isFetching: boolean;
};

/** El número grande con su detalle, o la invitación a elegir. */
function LiveNumbers({
  sale,
  preview,
  hasProducts,
}: {
  sale: SaleCopy;
  preview: PreviewState;
  hasProducts: boolean;
}) {
  if (!hasProducts) {
    return (
      <p className="text-[14px] leading-[1.5] text-blue-900/80">
        Dinos qué haces y te mostramos cuántas {sale.many} están convocadas hoy.
      </p>
    );
  }
  // Un conteo que falla no detiene a nadie: la tarjeta se queda sin números.
  if (preview.isError || (!preview.data && !preview.isFetching)) {
    return (
      <p className="text-[14px] leading-[1.5] text-blue-900/80">
        Los números aparecen en cuanto terminemos de buscar.
      </p>
    );
  }
  if (!preview.data) {
    return <div className="h-[92px] animate-pulse rounded-xl bg-blue-100/70" />;
  }

  const { works, amountPen, closingThisWeek, nextClosingDays } = preview.data;
  return (
    <>
      <div className="text-[56px] font-extrabold leading-none tracking-[-0.04em] text-blue-700">
        {formatNumber(works)}
      </div>
      <p className="mt-2 text-[15px] font-bold leading-[1.3] text-slate-900">
        {works === 1
          ? `${sale.one} convocada calza contigo`
          : `${sale.many} convocadas calzan contigo`}
      </p>
      <p className="mt-1.5 text-[13.5px] leading-[1.5] text-slate-600">
        {formatMoneyCompact(amountPen)} en juego · {closingThisWeek}{" "}
        {closingThisWeek === 1 ? "cierra" : "cierran"} el registro esta semana
        {nextClosingDays != null &&
          ` · la más próxima en ${nextClosingDays === 0 ? "hoy" : `${nextClosingDays} días`}`}
        .
      </p>
    </>
  );
}

/**
 * La línea de quien le vende al Estado que se va armando, con sus números en
 * vivo. En escritorio va a la derecha de la pregunta; en celular, debajo.
 */
export function ContractorLiveCard({
  sale,
  worksLabel,
  zone,
  size,
  preview,
}: {
  sale: SaleCopy;
  worksLabel: string | null;
  zone: string;
  /** Solo las obras eligen tamaño. */
  size: string | null;
  preview: PreviewState;
}) {
  const hasProducts = worksLabel != null;
  return (
    <aside className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_-20px_rgba(15,23,42,0.25)] sm:p-6 lg:sticky lg:top-8">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-bold text-slate-900">Tu línea de negocio</span>
        <span className="rounded-md bg-blue-700 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
          {sale.label}
        </span>
      </div>

      <dl className="mt-4 space-y-2.5 text-[13.5px]">
        <Row label="Rubro" value={worksLabel ?? "Por elegir"} muted={!hasProducts} />
        <Row label="Zona" value={zone} muted={false} />
        {size != null && <Row label="Tamaño" value={size} muted={size === "Cualquiera"} />}
      </dl>

      <div className="mt-5 rounded-[18px] bg-blue-50 p-5">
        <p className="mb-2 flex items-center gap-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-blue-700">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          En vivo · SEACE hoy
        </p>
        <LiveNumbers sale={sale} preview={preview} hasProducts={hasProducts} />
      </div>

      <p className="mt-4 text-[12px] text-slate-400">Se actualiza con cada respuesta.</p>
    </aside>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={
          muted ? "text-right font-semibold text-slate-400" : "text-right font-bold text-slate-900"
        }
      >
        {value}
      </dd>
    </div>
  );
}
